import { adminRoles } from '@/constants';
import { checkAndRedirectForOrgAdminAccess } from '@/helpers';
import { hasAnyOrgRole } from '@/helpers/roles';
import { apiClient } from '@/lib/api-client';
import { useAppStore } from '@/stores/app';
import { SAMLMetadata } from '@/types';
import type { SAMLProviderGetResponse } from '@/types';
import { createFileRoute, redirect } from '@tanstack/react-router';

export type SAMLProviderFormDefaults = {
  provider_name: string;
  email_domain: string;
  metadata_url?: string | null;
  metadata_file?: string | null;
  attr_email: string;
  attr_first_name: string;
  attr_last_name: string;
  attr_roles: string;
};

export const Route = createFileRoute('/auth/')({
  beforeLoad: ({ context, location }) => {
    const store = useAppStore.getState();
    // Use selectedOrganization (runtime) if available, else fall back to selectedOrgStub (persisted)
    const selectedOrganization = store.selectedOrganization || store.selectedOrgStub;
    checkAndRedirectForOrgAdminAccess({
      context,
      location,
      selectedOrganization,
      adminRoles,
      redirect,
    });
  },
  loader: async ({ context }) => {
    // `undefined`, never placeholder defaults: AuthPage seeds both as `initialData` on 24h-stale
    // queries, and query-core re-applies initialData the moment it stops being undefined — so a
    // placeholder here is a blank SAML config that never refetches. Same as /instruments' loader.
    const noLoaderData = { samlMetadata: undefined, providerDefaults: undefined };

    // beforeLoad returns without throwing while auth is still PENDING, so the loader can still
    // run in that window — gate the actual fetch here too (issue #396, same as /instruments).
    const store = useAppStore.getState();
    const selectedOrganization = store.selectedOrganization || store.selectedOrgStub;
    if (
      context.authentication.status !== 'AUTHENTICATED' ||
      !selectedOrganization?.id ||
      !hasAnyOrgRole(context.authentication.user?.assumed_roles, selectedOrganization.id, adminRoles)
    ) {
      return noLoaderData;
    }

    try {
      const [metadata, provider] = await Promise.all([
        apiClient.get<SAMLMetadata>('/auth/saml/metadata'),
        apiClient.get<SAMLProviderGetResponse>('/auth/saml/provider'),
      ]);

      let providerDefaults: SAMLProviderFormDefaults = {
        provider_name: '',
        email_domain: '',
        metadata_url: '',
        metadata_file: '',
        attr_email: '',
        attr_first_name: '',
        attr_last_name: '',
        attr_roles: '',
      };
      if (provider) {
        providerDefaults = {
          provider_name: provider.provider_name ?? '',
          email_domain: provider.email_domain ?? '',
          metadata_url: provider.metadata_url ?? '',
          metadata_file: provider.metadata_file ?? '',
          attr_email: provider.attribute_mapping?.email ?? '',
          attr_first_name: provider.attribute_mapping?.first_name ?? '',
          attr_last_name: provider.attribute_mapping?.last_name ?? '',
          attr_roles: provider.attribute_mapping?.roles ?? '',
        };
      }

      return {
        samlMetadata: {
          recipient: metadata?.recipient ?? '',
          audience: metadata?.audience ?? '',
        },
        providerDefaults,
      };
    } catch {
      return noLoaderData;
    }
  },
  // No component here! The component is loaded via index.lazy.tsx
});
